import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import {
  isAConversationRoute,
  isAInboxViewRoute,
} from 'dashboard/helper/routeHelpers';

const MAX_DISCONNECT_SECONDS = 10800;

// The disconnect delay threshold is added to account for delays in identifying
// disconnections (for example, the websocket disconnection takes up to 3 seconds)
// while fetching the latest updated conversations or messages.
const DISCONNECT_DELAY_THRESHOLD = 15;

// Periodic sync interval for active conversation messages (30 seconds)
const ACTIVE_CONVERSATION_SYNC_INTERVAL = 30000;

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    this.activeSyncTimer = null;

    this.setupEventListeners();
    this.startActiveConversationSync();
  }

  disconnect = () => {
    this.removeEventListeners();
    this.stopActiveConversationSync();
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
    }
  };

  fetchConversations = async () => {
    await this.store.dispatch('updateChatListFilters', {
      page: null,
      updatedWithin:
        this.getSecondsSinceDisconnect() + DISCONNECT_DELAY_THRESHOLD,
    });
    await this.store.dispatch('fetchAllConversations');
    // Reset the updatedWithin in the store chat list filter after fetching conversations when the user is reconnected
    await this.store.dispatch('updateChatListFilters', {
      updatedWithin: null,
    });
  };

  fetchFilteredOrSavedConversations = async queryData => {
    try {
      await this.store.dispatch('fetchFilteredConversations', {
        queryData,
        page: 1,
      });
    } catch (error) {
      // Ignore error, reconnect flow should continue
    }
  };

  fetchConversationsOnReconnect = async () => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations();
    }
  };

  fetchConversationMessagesOnReconnect = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect();
      await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    }
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime = new Date();
    this.setConversationLastMessageId();
  };

  onReconnect = async () => {
    await this.handleRouteSpecificFetch();
    await this.revalidateCaches();
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
  };

  startActiveConversationSync = () => {
    this.stopActiveConversationSync();
    this.activeSyncTimer = setInterval(async () => {
      const { conversation_id: conversationId } =
        this.router.currentRoute.value.params;
      if (!conversationId) return;

      const numericId = Number(conversationId);
      const { allConversations } = this.store.state.conversations;
      const chat = allConversations.find(c => c.id === numericId);
      if (!chat || !chat.messages || chat.messages.length === 0) return;

      const lastMessage = chat.messages[chat.messages.length - 1];
      if (!lastMessage) return;

      try {
        const { default: MessageApi } = await import(
          'dashboard/api/inbox/message'
        );
        const {
          data: { payload },
        } = await MessageApi.getPreviousMessages({
          conversationId: numericId,
          after: lastMessage.id,
        });

        if (payload && payload.length > 0) {
          const missingMessages = payload.filter(
            msg => !chat.messages.find(m => m.id === msg.id)
          );
          if (missingMessages.length > 0) {
            missingMessages.forEach(msg => {
              this.store.dispatch('addMessage', msg);
            });
          }
        }

        // Also update existing messages that may have changed (e.g. audio transcriptions)
        this.syncUpdatedMessages(chat, numericId);
      } catch {
        // Silently handle sync errors
      }
    }, ACTIVE_CONVERSATION_SYNC_INTERVAL);
  };

  syncUpdatedMessages = async (chat, conversationId) => {
    // Check last 10 messages for updates (covers recent audio transcriptions)
    const recentMessages = chat.messages.slice(-10);
    if (recentMessages.length === 0) return;

    try {
      const { default: MessageApi } = await import(
        'dashboard/api/inbox/message'
      );
      // Fetch from the oldest recent message to get all updates
      const oldestRecent = recentMessages[0];
      const {
        data: { payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: oldestRecent.id - 1,
      });

      if (!payload || payload.length === 0) return;

      payload.forEach(serverMsg => {
        const idx = chat.messages.findIndex(m => m.id === serverMsg.id);
        if (idx === -1) return;

        const localMsg = chat.messages[idx];
        // Update if server version is newer
        if (
          serverMsg.updated_at &&
          localMsg.updated_at &&
          serverMsg.updated_at > localMsg.updated_at
        ) {
          this.store.dispatch('updateMessage', serverMsg);
        }
      });
    } catch {
      // Silently handle errors
    }
  };

  stopActiveConversationSync = () => {
    if (this.activeSyncTimer) {
      clearInterval(this.activeSyncTimer);
      this.activeSyncTimer = null;
    }
  };
}

export default ReconnectService;
